import { RichTextValue } from "@xapp/chat-widget-core";
import React, { FC, useCallback, useEffect, useState, useRef } from "react";
import "./SearchInput.scss";
import { ResponseOutput } from "stentor-models";
import { SearchResponseText } from "../SearchResponseText";
import { TypingIndicator } from "../TypingIndicator";
import { LordIcon } from "../LordIcon";

// import { LordIconStatic } from "../LordIcon/LordIcon";

export interface SearchInputProps {
    readonly value: RichTextValue;
    readonly autoFocus?: boolean;
    readonly audioOn: boolean;
    onSearch?(value: RichTextValue): void;
    onChange(value: RichTextValue): void;
    onPrev(): void;
    onNext(): void;
    onSubmit(value: RichTextValue): void;
    onCancel?(): void;
    onFocus?(): void;
    outputSpeech: string | ResponseOutput | undefined;
    loading: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// let speech: any;
// if ((window as Window).webkitSpeechRecognition) {
//         const SpeechRecognition = (window as Window).webkitSpeechRecognition;
//     speech = new SpeechRecognition();
//     speech.continuous = false; // stop at the silence
//     speech.lang = "en-US";
// }

export const SearchInput: FC<SearchInputProps> = (props) => {
    const { value, audioOn, onChange, onSubmit } = props;

    const speech = useRef<any>();

    let playMethod: () => void = () => {};
    let stopMethod: () => void = () => {};

    const handleChange = useCallback(
        (val: RichTextValue) => {
            onChange(val);
        },
        [onChange]
    );

    // TODO: icon change/animation
    // const [isListening, setIsListening] = useState(false);

    const [isFirstRender, setIsFirstRender] = useState(true);
    useEffect(() => {
      if (isFirstRender) {
        setIsFirstRender(false);
      } 
    }, [isFirstRender]); 
  
    useEffect(() => {
        if ('webkitSpeechRecognition' in window) {
            const recognition = new window.webkitSpeechRecognition();
            recognition.continuous = false;
            recognition.lang = 'en-US';
            speech.current = recognition;
        }

        if (!speech.current) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        speech.current.onresult = (event: any) => {
            console.log("###### STOP: onresult");
            dontListen();

            // const text = event.results[event.results.length - 1][0].transcript;
            const text = event.results[0][0].transcript;

            if (text && text.trim().length > 0) {
                onSubmit({ text: text, formats: [] }); // send it
            } else {
                handleChange({ text: "", formats: [] }); // wipe it off - no edit
            }
        };

        speech.current.onspeechend = function () {
            console.log("###### STOP: onspeechend");
            dontListen();
        };

        speech.current.onend = function () {
            console.log("###### STOP: onend");
            dontListen();
        };

        speech.current.onerror = function (event: any) {
            console.error(`Speech recognition error detected: ${event.error}`);
            dontListen();
        };

        speech.current.onnomatch = function (_event: unknown) {
            console.log("###### STOP: onnomatch");
            dontListen();
        };
    }, []);

    const dontListen = () => {
        // setIsListening(false);
        // speech.current.stop();
        stopMethod();  
    };

    const listen = () => {
        if (!audioOn && !loading) {
            // setIsListening(true);
            try {
                speech.current.start();
                playMethod();
            } catch (err) {
                console.warn("Mic was on already!");
            }
        }
    };

    const { outputSpeech, loading } = props;
    
    // We are looking for a on/off transition of audioOn
    useEffect(() => {
        if (!audioOn && !loading && !isFirstRender) {
            // setIsListening(true);
            try {
                speech.current.start();
                playMethod();
            } catch (err) {
                console.warn("Mic was on already!");
            }
        } else {
            console.log(`###### STOP: audioOn = ${audioOn} && loading = ${loading}`);
            dontListen();
        }
    }, [audioOn]);

    const [isMultiline, setIsMultiline] = useState(false);
    const hiddenRef = useRef<HTMLParagraphElement | null>(null);

    useEffect(() => {
        const paragraph = hiddenRef.current;
        if (paragraph) {
            const style = window.getComputedStyle(paragraph);
            const lineHeight = parseInt(style.lineHeight, 10);
            const height = paragraph.clientHeight;
            setIsMultiline(height > lineHeight);
        } 
    }, [hiddenRef.current?.clientHeight]);

    const text = (typeof outputSpeech === "string" ? outputSpeech : outputSpeech?.displayText);

     return (
        <div className="xapp-search-input">
            <div className="xapp-search-input__lord" onClick={listen}>
                {/* {isListening ? (
                    <LordIcon
                        src="https://cdn.lordicon.com/fpipqhrr.json"
                        trigger="loop"
                        colors={{ primary: "#4287d8", secondary: "#4287d8" }}
                        size="40px"
                        delay={300}
                    />
                ) : (
                    <LordIconStatic size="40px" />
                )} */}

                <LordIcon
                    onPlay={(play) => (playMethod = play)}
                    onStop={(stop) => (stopMethod = stop)}
                    className="visible"
                    src="https://cdn.lordicon.com/fpipqhrr.json"
                    trigger="loop"
                    colors={{ primary: "#4287d8", secondary: "#4287d8" }}
                    size="40px"
                    delay={300}
                />
            </div>

            <div className="xapp-search-input__message-container">
                {(!isMultiline || loading) && (
                    <div className="xapp-search-input__message-input">
                        <SearchResponseText text={value.text} pClass="blue" />
                    </div>
                )}

                <div ref={hiddenRef} className="xapp-search-input__message-output">
                    {loading ? (
                        <div className="xapp-search-widget__spinner">
                            <TypingIndicator />
                        </div>
                    ) : (
                        <>{!!text && <SearchResponseText text={text} />}</>
                    )}
                </div>
            </div>
        </div>
    );
};
